import { useState, useRef, useEffect } from 'react';
import { LucideCircleQuestionMark } from 'lucide-react';
import { format } from "date-fns";
import moment from "moment-timezone";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ToggleSwitch from '../../components/ToggleSwitch';
import ReorderIcon from '../../../../../assets/images/icons/reorder.svg'
import DateCalendar from '../../components/DateCalender';

const SortablePlatformRow = ({ platform, index, onToggle }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: platform.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 20 : undefined,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={`flex items-center justify-between gap-3 p-3 border border-theme_clr1 rounded-lg bg-white touch-none cursor-grab active:cursor-grabbing
                ${!platform.enabled ? "opacity-50" : ""}
                ${isDragging ? "shadow-lg" : ""}
            `}
        >
            <div className="flex items-center gap-3 min-w-0">
                <img src={ReorderIcon} alt="" className="shrink-0" />

                {platform.background ? (
                    <div
                        className={`w-10 h-10 shrink-0 ${platform.background} rounded-lg flex items-center justify-center`}
                    >
                        <img alt="" src={platform.icon} className="w-6 h-6" />
                    </div>
                ) : (
                    <img alt="" src={platform.icon} className="w-10 h-10 shrink-0" />
                )}

                <div className="min-w-0">
                    <h4 className="font-bold text-theme_clr1">
                        {platform.label}
                    </h4>
                    <p className="text-xs font-semibold text-theme_clr2">
                        {platform.text}
                    </p>
                </div>
            </div>

            {index !== 0 && (
                <div
                    className="shrink-0 cursor-pointer"
                    onPointerDown={(event) => event.stopPropagation()}
                >
                    <ToggleSwitch
                        enabled={platform.enabled}
                        setEnabled={() => onToggle(platform.id)}
                        size="md"
                        onColor="bg-theme_clr2"
                        offColor="bg-gray-300"
                    />
                </div>
            )}
        </div>
    );
};


export const DetailsStep = ({ preSaveLinkData, setPreSaveLinkData, platformsState, setPlatformsState }) => {
    const [showCalendar, setShowCalendar] = useState(false)
    const calendarRef = useRef(null);
    const TIMEZONES = moment.tz.names();
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                showCalendar &&
                calendarRef.current &&
                !calendarRef.current.contains(event.target)
            ) {
                setShowCalendar(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showCalendar]);

    const handleInputChange = (field, value) => {
        setPreSaveLinkData(prev => ({ ...prev, [field]: value }));
    };
    const handleTypeChange = (type) => {
        setPreSaveLinkData((prev) => ({
            ...prev,
            scanLinkType: type,
            scanLink: "", // reset value when switching type
        }));
    };

    const prefixMap = {
        link: "",
        upc: "UPC:",
        isrc: "ISRC:",
    };

    const placeholderMap = {
        link: "Paste release link",
        upc: "Enter UPC number",
        isrc: "Enter ISRC code",
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 5,
            },
        })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setPlatformsState((prev) => {
            const oldIndex = prev.findIndex((p) => p.id === active.id);
            const newIndex = prev.findIndex((p) => p.id === over.id);
            return arrayMove(prev, oldIndex, newIndex).map((p, index) => ({
                ...p,
                order: index + 1,
            }));
        });
    };

    const togglePlatform = (id) => {
        setPlatformsState((prev) =>
            prev.map((p) =>
                p.id === id ? { ...p, enabled: !p.enabled } : p
            )
        );
    };


    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-bold text-theme_clr1 mb-1">Release Details</h2>
                <p className="text-theme_clr2 font-semibold text-sm">Add in your release details to automatically setup your pre-save page.</p>
                <hr className="border-t border-theme_clr1 my-4" />
            </div>

            <div data-tour="collect-fan-data">
                <h3 className="text-lg font-bold text-theme_clr2">Power Ups</h3>
                <p className="text-sm text-theme_clr1 font-semibold mb-2">Enable boosts to make it even easier to grow.</p>
                <hr className="border-t border-theme_clr2 my-2" />

                <div className="space-y-4">
                    {/* Collect Fan Data */}
                    <div>
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <h4 className="font-bold text-theme_clr1 mb-1">
                                    Collect Fan Data
                                </h4>

                                <p className="text-sm text-theme_clr2 font-semibold">
                                    When enabled, fans who pre-save will be asked to subscribe and consent to sharing their fan data, including name, email, and location.
                                </p>
                            </div>

                            <ToggleSwitch
                                enabled={preSaveLinkData.emailCollection.enabled}
                                setEnabled={(val) => {
                                    setPreSaveLinkData(prev => ({
                                        ...prev,
                                        emailCollection: {
                                            ...prev.emailCollection,
                                            enabled: val,
                                            actionName: val
                                                ? prev.emailCollection.actionName || "Subscribe"
                                                : ""
                                        }
                                    }));
                                }}
                                size="md"
                                onColor="bg-theme_clr2"
                                className="email_collection"
                            />
                        </div>

                        {/* Button Text Input */}
                        {preSaveLinkData.emailCollection.enabled && (
                            <div className="mt-3">
                                <label className="text-sm font-semibold text-theme_clr1">
                                    Email Button Text
                                </label>

                                <input
                                    type="text"
                                    value={preSaveLinkData.emailCollection.actionName || ""}
                                    onChange={(e) =>
                                        setPreSaveLinkData(prev => ({
                                            ...prev,
                                            emailCollection: {
                                                ...prev.emailCollection,
                                                actionName: e.target.value
                                            }
                                        }))
                                    }
                                    className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-theme_clr2"
                                    placeholder="Subscribe"
                                />
                            </div>
                        )}
                    </div>
                    <hr className="border-t border-theme_clr2 my-4" />
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <h4 className="font-bold text-theme_clr1 mb-1">Boost Followers</h4>
                            <p className="text-sm text-theme_clr2 font-semibold">When enabled, fans who pre-save will automatically follow your streaming service profile.</p>
                        </div>
                        <ToggleSwitch
                            enabled={preSaveLinkData.preSaveFollow}
                            setEnabled={(value) => {
                                handleInputChange('preSaveFollow', value)
                            }}
                            size="md"
                            onColor='bg-theme_clr2'
                            className='boost_followers'
                        />
                    </div>
                    <hr className="border-t border-theme_clr2 my-4" />
                    <div className="flex items-start justify-between">
                        <div className="flex-1 flex items-center gap-2">
                            <h4 className="font-bold text-theme_clr1">Future Saves</h4>
                            <LucideCircleQuestionMark className="w-6 h-6 text-theme_clr2" />
                        </div>
                        <ToggleSwitch
                            enabled={preSaveLinkData.enableFutureSave}
                            setEnabled={(value) => {
                                handleInputChange('enableFutureSave', value)
                            }}
                            size="md"
                            onColor='bg-theme_clr2'
                            className='future_save'
                        />
                    </div>
                    <p className="text-sm text-theme_clr2 font-semibold">Grow saves with every release by asking fans if they want to automatically save your future releases.</p>
                </div>
            </div>
            <hr className="border-t border-theme_clr2 my-4" />
            <div data-tour="release-links">
                <h3 className="text-lg font-bold text-theme_clr1 mb-2">UPC / ISRC / Link</h3>

                {/* TYPE SELECTOR */}
                <div className="flex gap-2 mb-3">
                    {["url", "upc", "isrc"].map((type) => (
                        <button
                            key={type}
                            type="button"
                            onClick={() => handleTypeChange(type)}
                            className={`px-4 py-1 rounded-full border text-sm font-semibold transition
                                       ${preSaveLinkData.scanLinkType === type
                                    ? "bg-theme_clr1 text-white border-theme_clr1"
                                    : "bg-white text-theme_clr1 border-theme_clr1"
                                }`}
                        >
                            {type.toUpperCase()}
                        </button>
                    ))}
                </div>

                <p className="text-sm mb-3">
                    <span className="text-theme_clr2 font-semibold">
                        Enter your release {preSaveLinkData.scanLinkType.toUpperCase()}.
                    </span>
                    <br />
                    <span className="text-theme_clr1 font-semibold">
                        Need help finding this info?
                    </span>
                </p>

                {/* INPUT WITH PREFIX */}
                <div className="flex items-center border border-theme_clr1 rounded-lg px-3 py-2">
                    {prefixMap[preSaveLinkData.scanLinkType] && (
                        <span className="text-theme_clr1 font-semibold mr-2">
                            {prefixMap[preSaveLinkData.scanLinkType]}
                        </span>
                    )}

                    <input
                        type="text"
                        value={preSaveLinkData.scanLink}
                        placeholder={placeholderMap[preSaveLinkData.scanLinkType]}
                        onChange={(e) => handleInputChange('scanLink', e.target.value)}
                        className="w-full outline-none placeholder-gray-500"
                    />
                </div>

                <p className="text-sm text-theme_clr2 font-semibold mt-2">
                    You can always add this info after setting up your campaign.
                </p>
            </div>


            <div data-tour="release-date">
                <h3 className="text-lg font-bold text-theme_clr1 mb-2">Release Date</h3>
                <p className="text-sm text-theme_clr2 font-semibold mb-3">
                    Enter the release date & time for your song / album – this is when it will be added to fans' libraries.
                </p>

                {/* Date + Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Release Date */}
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setShowCalendar(!showCalendar)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 rounded"
                        >
                            <i className="bi bi-calendar-event text-theme_clr1"></i>
                        </button>

                        <input
                            type="text"
                            placeholder="YYYY-MM-DD"
                            value={preSaveLinkData.releaseDate}
                            className="w-full px-4 py-2 border border-theme_clr1 rounded-lg text-theme_clr1 bg-white cursor-pointer focus:outline-none focus:border-2 focus:border-theme_clr1"
                            onClick={() => setShowCalendar(true)}
                        />

                        {showCalendar && (
                            <div
                                ref={calendarRef}
                                className="absolute top-[110%] left-0 z-50"
                            >
                                <DateCalendar
                                    selectedDate={preSaveLinkData.releaseDate}
                                    onSelectDate={(date) => {
                                        handleInputChange("releaseDate", format(date, "yyyy-MM-dd"));
                                        setShowCalendar(false);
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Release Time */}
                    <input
                        type="time"
                        value={preSaveLinkData.releaseTime}
                        onChange={(e) =>
                            handleInputChange("releaseTime", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-theme_clr1 rounded-lg text-theme_clr1 bg-white focus:outline-none focus:border-2 focus:border-theme_clr1"
                    />
                </div>

                {/* Timezone */}
                <div className="mb-4">
                    <label className="block text-sm font-bold text-theme_clr1 mb-2">
                        Timezone
                    </label>

                    <select
                        value={preSaveLinkData.timezone}
                        onChange={(e) =>
                            handleInputChange("timezone", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-theme_clr1 rounded-lg bg-white text-theme_clr1 focus:outline-none focus:border-2 focus:border-theme_clr1"
                    >
                        {TIMEZONES.map((tz) => (
                            <option key={tz} value={tz}>
                                {tz}
                            </option>
                        ))}
                    </select>

                    <p className="text-xs text-theme_clr2 font-semibold mt-1">
                        Used to determine the exact release moment globally.
                    </p>
                </div>

                {/* Hide release date */}
                {/* <label className="flex items-center text-sm">
                    <input
                        type="checkbox"
                        checked={preSaveLinkData.hideReleaseDate}
                        onChange={(e) =>
                            handleInputChange("hideReleaseDate", e.target.checked)
                        }
                        className="mr-2"
                    />
                    <span className="text-theme_clr1 font-semibold">
                        Hide release date from your page
                    </span>
                </label> */}
            </div>

            <hr className="border-t border-theme_clr1 my-3" />
            <div data-tour="pre-save-message">
                <h3 className="text-lg font-bold text-theme_clr1 mb-2">Message to show after successful pre-save</h3>
                <textarea
                    rows={2}
                    placeholder="Successful pre-save message"
                    value={preSaveLinkData?.saveSuccessfulMessage}
                    onChange={(e) =>
                        handleInputChange("saveSuccessfulMessage", e.target.value)
                    }
                    className="w-full px-4 py-2 placeholder-gray-500 border border-theme_clr1 rounded-lg resize-none focus:outline-none focus:border-2 focus:border-theme_clr1"
                ></textarea>
            </div>

            <hr className="border-t border-theme_clr1 my-3" />
            <div className="space-y-4" data-tour="fan-notification-details">
                <h3 className="text-lg font-bold text-theme_clr1">
                    Fan Notification Details
                </h3>

                <p className="text-sm text-theme_clr2 font-semibold">
                    Customize the email fans receive when your release goes live.
                </p>

                {/* Notification Subject */}
                <div>
                    <label className="block text-sm font-bold text-theme_clr1 mb-1">
                        Notification Subject
                    </label>
                    <input
                        type="text"
                        placeholder="Your new release is out now!"
                        value={preSaveLinkData.notificationSubject}
                        onChange={(e) =>
                            handleInputChange("notificationSubject", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-theme_clr1 rounded-lg focus:outline-none focus:border-2 focus:border-theme_clr1"
                    />
                </div>

                {/* Notification Message */}
                <div>
                    <label className="block text-sm font-bold text-theme_clr1 mb-1">
                        Notification Message
                    </label>
                    <textarea
                        rows={2}
                        placeholder="Thanks for pre-saving! Your release is now available in your library."
                        value={preSaveLinkData.notificationMessage}
                        onChange={(e) =>
                            handleInputChange("notificationMessage", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-theme_clr1 rounded-lg resize-none focus:outline-none focus:border-2 focus:border-theme_clr1"
                    />
                </div>

                {/* Reserved for future fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* <div>
                        <label className="block text-sm font-bold text-theme_clr1 mb-1">
                            Sender Email (optional)
                        </label>
                        <input
                            type="text"
                            placeholder="Artist / Label name"
                            onChange={(e) =>
                                handleInputChange("notificationSenderEmail", e.target.value)
                            }
                            className="w-full px-4 py-2 border border-theme_clr1 rounded-lg resize-none focus:outline-none focus:border-2 focus:border-theme_clr1"
                        />
                    </div> */}
                    <div>
                        <label className="block text-sm font-bold text-theme_clr1 mb-1">
                            Footer Text (optional)
                        </label>
                        <input
                            type="text"
                            placeholder="Company address or legal text"
                            value={preSaveLinkData.notificationFooter}
                            onChange={(e) =>
                                handleInputChange("notificationFooter", e.target.value)
                            }
                            className="w-full px-4 py-2 border border-theme_clr1 rounded-lg resize-none focus:outline-none focus:border-2 focus:border-theme_clr1"
                        />
                    </div>
                </div>
            </div>

            {/* <div className="bg-[#333898] rounded-lg p-4 flex items-center gap-3">
                <div className="bg-theme_clr1 rounded-full p-2 flex-shrink-0">
                    <img src={LocateIcon} />
                </div>
                <div>
                    <div className="flex-1 flex items-center gap-2">
                        <h4 className="font-bold text-theme_clr2 mb-1">Realtime Release </h4>
                        <LucideCircleQuestionMark className='w-6 h-6 text-theme_clr2' />
                    </div>
                    <div className='flex'>
                        <p className="text-sm text-white">Automatically convert your pre-save into a "Listen Now" music site for fans where your song / album has already released.</p>
                        <ToggleSwitch
                            enabled={titleSwitchStatus}
                            setEnabled={setTitleSwitchStatus}
                            size="md"
                            onColor='bg-theme_clr2'
                            offColor='bg-theme_clr2'
                        />
                    </div>
                </div>
            </div> */}

            <div data-tour="custom-links">
                <h3 className="text-lg font-bold text-theme_clr1 mb-2">
                    Music Platforms & Custom Links
                </h3>

                <p className="text-sm text-gray-500 font-semibold mb-4">
                    Select the platforms you want fans to be able to save on and add custom links.
                </p>

                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={platformsState.map((platform) => platform.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="flex flex-col gap-3">
                            {platformsState.map((platform, index) => (
                                <SortablePlatformRow
                                    key={platform.id}
                                    platform={platform}
                                    index={index}
                                    onToggle={togglePlatform}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            </div>

        </div>
    )
}